"use client"

import { useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { TkMonogram } from "@/components/ui/TkMonogram"
import { MobileNav } from "./MobileNav"

export interface MicroSiteLink {
  label: string
  href: string
}

const serviceLink = (active: boolean) =>
  `tk-nav-grow whitespace-nowrap text-xs uppercase tracking-[0.05em] ${
    active
      ? "font-bold text-tk-slate"
      : "font-medium text-tk-slate/80 hover:text-tk-slate"
  }`

const eyebrowLink = (active: boolean) =>
  `tk-nav-grow whitespace-nowrap text-[11px] font-ui uppercase tracking-[0.12em] ${
    active
      ? "font-bold text-tk-linen"
      : "font-medium text-tk-linen/90 hover:text-tk-linen hover:underline"
  }`

interface HeaderProps {
  /* Service micro-site mode: the eyebrow flips from the onyx studio bar to a
     solid teal bar of on-page anchor links, the drawer gains an "On this
     page" section, and `name` renders as "TALLKAROL   |   NAME" in the
     lockup. The active service link doubles as the "you are here" marker. */
  microsite?: { name: string; links: MicroSiteLink[] }
}

const serviceNavItems: { href: string; label: string }[] = [
  { href: "/services/web-development", label: "WEBSITES" },
  { href: "/services/app-development", label: "APPS" },
  { href: "/services/systems-integration", label: "SYSTEMS" },
  /* AGENTS routes to the HiveMind flagship; AI Integration lives under the
     SYSTEMS group in MobileNav now. */
  { href: "/services/ai-hivemind", label: "AGENTS" },
]

export function Header({ microsite }: HeaderProps = {}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const pathname = usePathname()
  const portfolioActive = pathname === "/portfolio" || pathname.startsWith("/portfolio/")
  const howActive = pathname === "/how-it-works"
  const whyActive = pathname === "/why" || pathname.startsWith("/why/")

  return (
    <>
      <header className="sticky top-0 z-40 bg-tk-linen/90 backdrop-blur-sm">
        <nav className="mx-auto flex max-w-7xl items-center justify-between border-b border-tk-slate/10 px-6 py-3 sm:px-6 lg:px-8">
          {/* Logo + wordmark, plus the service name on micro-sites */}
          <div className="flex items-center">
            <Link href="/" className="group flex items-center gap-1.5">
              <TkMonogram className="h-8 w-8" />
              <span className="tk-nav-grow origin-left text-sm font-semibold uppercase tracking-[0.15em] text-tk-teal font-ui">
                TALLKAROL
              </span>
            </Link>
            {microsite && (
              /* whitespace-pre keeps the literal spaces around the pipe; the
                 long names collide with the right-side links below lg. text-xs
                 is 2px under the wordmark's text-sm. */
              <span className="hidden whitespace-pre text-xs font-semibold uppercase tracking-[0.15em] text-tk-teal font-ui lg:inline">
                <span aria-hidden="true">{"   |   "}</span>
                {microsite.name}
              </span>
            )}
          </div>

          {/* Desktop — md is enough now that the four services + CTA are the
              only items on this row. */}
          <div className="hidden items-center gap-6 font-ui md:flex">
            {serviceNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                prefetch={false}
                className={serviceLink(pathname.startsWith(item.href))}
              >
                {item.label}
              </Link>
            ))}
            <Link
              href="/contact#intake"
              prefetch={false}
              data-track="cta"
              data-track-id="header-get-in-touch"
              data-track-loc="header"
              className="tk-nav-grow whitespace-nowrap rounded-full bg-tk-teal px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.05em] text-tk-linen hover:bg-tk-teal/90"
            >
              Get in Touch
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden p-2 hover:bg-tk-slate/5 rounded-lg transition-colors"
            aria-label="Open menu"
          >
            <svg
              className="w-6 h-6 text-tk-slate"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </nav>

        {/* Eyebrow under the main nav. Hidden below md so the mobile header
            stays 57px; its links already live in the drawer. Height is locked
            at 32px so --tk-header-h can be an exact sum.

            Two modes: studio pages get the onyx bar with the three studio
            links; service micro-sites get a solid teal bar with the page's
            anchor sublinks (linen/90 on teal is the audited pairing — the /90
            floor from CLAUDE.md; hover underlines, never dims). */}
        {microsite ? (
          <div className="hidden h-8 border-b border-tk-linen/20 bg-tk-teal md:block">
            <nav
              aria-label="On this page"
              className="mx-auto flex h-full max-w-7xl items-center justify-center gap-5 px-6 lg:px-8"
            >
              {microsite.links.map((link, i) => (
                <span key={link.href} className="contents">
                  {i > 0 && (
                    <span aria-hidden="true" className="text-[11px] text-tk-linen/90">
                      ·
                    </span>
                  )}
                  <a href={link.href} className={eyebrowLink(false)}>
                    {link.label}
                  </a>
                </span>
              ))}
            </nav>
          </div>
        ) : (
          <div className="hidden h-8 border-b border-tk-linen/20 bg-tk-onyx md:block">
            <nav
              aria-label="About"
              className="mx-auto flex h-full max-w-7xl items-center justify-center gap-5 px-6 lg:px-8"
            >
              <Link href="/how-it-works" prefetch={false} className={eyebrowLink(howActive)}>
                How It Works
              </Link>
              <span aria-hidden="true" className="text-[11px] text-tk-linen/50">
                ·
              </span>
              <Link href="/portfolio" prefetch={false} className={eyebrowLink(portfolioActive)}>
                What I&apos;ve Done
              </Link>
              <span aria-hidden="true" className="text-[11px] text-tk-linen/50">
                ·
              </span>
              <Link href="/why" prefetch={false} className={eyebrowLink(whyActive)}>
                Why Choose Me
              </Link>
            </nav>
          </div>
        )}
      </header>
      <MobileNav
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        micrositeLinks={microsite?.links}
      />
    </>
  )
}
